/**
 * TeamSystem Impact - Stakeholder Assessment API Client
 * API calls for multi-stakeholder IRO assessments
 */

import { apiClient } from '../../../api/client';
import { AxiosError } from 'axios';
import type { ESRSStandard } from './iro.api';

/**
 * Helper to extract error message from API response
 */
function extractErrorMessage(error: unknown, defaultMessage: string): string {
  if (error instanceof AxiosError && error.response?.data) {
    const data = error.response.data as { error?: string; message?: string };
    return data.error || data.message || defaultMessage;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return defaultMessage;
}

// Types
export type AssessmentType = 'impact' | 'financial' | 'both';
export type StakeholderAssessmentStatus =
  | 'pending'
  | 'carried_over'
  | 'completed'
  | 'revised'
  | 'skipped';
export type AssessorConfigStatus = 'open' | 'closed';

export interface StakeholderImpactAssessment {
  scale: number;
  scope: number;
  irremediability?: number;
  probability?: number;
  impactScore: number;
  notes?: string;
}

export interface StakeholderFinancialAssessment {
  likelihood: number;
  ecoFinancialImpact?: number;
  reputationalImpact: number;
  operationalImpact: number;
  financialScore: number;
  notes?: string;
}

export interface StakeholderAssessment {
  _id?: string;
  iroId: string;
  aziendaId: string;
  assessorId: string;
  assessorName: string;
  assessorRole: string;
  secondaryAssessors?: string[];
  assessmentType: AssessmentType;
  weight: number;
  impactAssessment?: StakeholderImpactAssessment;
  financialAssessment?: StakeholderFinancialAssessment;
  status: StakeholderAssessmentStatus;
  submittedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AssessorConfig {
  userId: string;
  userName: string;
  assessmentType: AssessmentType;
  weight: number;
  assignedAt: string;
  assignedBy: string;
}

export interface IROAssessorConfig {
  _id?: string;
  iroId: string;
  aziendaId: string;
  assessors: AssessorConfig[];
  assessmentDeadline?: string;
  status: AssessorConfigStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface ConsolidatedScores {
  impactScore: number;
  financialScore: number;
  overallScore: number;
  impactAssessmentCount: number;
  financialAssessmentCount: number;
  lastConsolidatedAt: string;
}

export interface ConfigureAssessorsDTO {
  assessors: Array<{
    userId: string;
    userName: string;
    assessmentType: AssessmentType;
    weight: number;
  }>;
  assessmentDeadline?: string;
}

export interface AddAssessorDTO {
  userId: string;
  userName: string;
  assessmentType: AssessmentType;
  weight: number;
}

export interface UpdateAssessorDTO {
  assessmentType?: AssessmentType;
  weight?: number;
}

export interface SubmitImpactAssessmentDTO {
  scale: number;
  scope: number;
  irremediability?: number;
  probability?: number;
  notes?: string;
}

export interface SubmitFinancialAssessmentDTO {
  likelihood: number;
  ecoFinancialImpact?: number;
  reputationalImpact?: number;
  operationalImpact?: number;
  notes?: string;
}

export interface SubmitStakeholderAssessmentDTO {
  impactAssessment?: SubmitImpactAssessmentDTO;
  financialAssessment?: SubmitFinancialAssessmentDTO;
  reopenIfFinalized?: boolean;
  onBehalfOfUserId?: string;
  assessorName?: string;
}

export interface StakeholderAssessmentListResponse {
  assessments: StakeholderAssessment[];
  config?: IROAssessorConfig;
  progress: {
    total: number;
    completed: number;
    pending: number;
  };
}

export interface AssignedIRO {
  iroId: string;
  iroCode: string;
  iroName: string;
  esrsStandard: ESRSStandard;
  assessmentType: AssessmentType;
  deadline?: string;
  status: StakeholderAssessmentStatus;
  myAssessment?: StakeholderAssessment;
  // Added for admin view of all assessments
  assessorId?: string;
  assessorName?: string;
  // Assignment status from campaign: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  assignmentStatus?: string;
}

export interface MyAssignedIROsResponse {
  assigned: AssignedIRO[];
  total: number;
  pending: number;
  carriedOver: number;
  completed: number;
  skipped: number;
}

/**
 * Assessment entity as returned by GET /assessments/list
 * This represents the actual StakeholderAssessment document with populated IRO fields.
 */
export interface AssessmentListItem {
  _id: string;
  iroId: string;
  iroCode: string;
  iroName: string;
  esrsStandard: ESRSStandard;
  iroType: string;
  assessorId: string;
  assessorName: string;
  assessorRole: string;
  secondaryAssessors?: string[];
  assessmentType: AssessmentType;
  weight: number;
  impactAssessment: StakeholderImpactAssessment | null;
  financialAssessment: StakeholderFinancialAssessment | null;
  status: StakeholderAssessmentStatus;
  notifiedAt?: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

const BASE_PATH = '/dma/stakeholder';

export const stakeholderAssessmentApi = {
  /**
   * Configura i valutatori per un IRO
   */
  configureAssessors: async (
    iroId: string,
    data: ConfigureAssessorsDTO
  ): Promise<IROAssessorConfig> => {
    try {
      const response = await apiClient.post<ApiResponse<IROAssessorConfig>>(
        `${BASE_PATH}/iro/${iroId}/assessors`,
        data
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to configure assessors');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante la configurazione dei valutatori'));
    }
  },

  /**
   * Ottieni configurazione valutatori per un IRO
   */
  getAssessorConfig: async (iroId: string): Promise<IROAssessorConfig | null> => {
    const response = await apiClient.get<ApiResponse<IROAssessorConfig | null>>(
      `${BASE_PATH}/iro/${iroId}/assessors`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get assessor config');
    return response.data.data || null;
  },

  /**
   * Aggiunge un valutatore a un IRO
   */
  addAssessor: async (iroId: string, data: AddAssessorDTO): Promise<IROAssessorConfig> => {
    try {
      const response = await apiClient.post<ApiResponse<IROAssessorConfig>>(
        `${BASE_PATH}/iro/${iroId}/assessors/add`,
        data
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to add assessor');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante l\'aggiunta del valutatore'));
    }
  },

  /**
   * Aggiorna un valutatore
   */
  updateAssessor: async (
    iroId: string,
    assessorId: string,
    data: UpdateAssessorDTO
  ): Promise<IROAssessorConfig> => {
    try {
      const response = await apiClient.put<ApiResponse<IROAssessorConfig>>(
        `${BASE_PATH}/iro/${iroId}/assessors/${assessorId}`,
        data
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to update assessor');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante l\'aggiornamento del valutatore'));
    }
  },

  /**
   * Rimuove un valutatore
   */
  removeAssessor: async (iroId: string, assessorId: string): Promise<IROAssessorConfig> => {
    try {
      const response = await apiClient.delete<ApiResponse<IROAssessorConfig>>(
        `${BASE_PATH}/iro/${iroId}/assessors/${assessorId}`
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to remove assessor');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante la rimozione del valutatore'));
    }
  },

  /**
   * Invia una valutazione stakeholder
   */
  submitAssessment: async (
    iroId: string,
    data: SubmitStakeholderAssessmentDTO
  ): Promise<StakeholderAssessment> => {
    try {
      const response = await apiClient.post<ApiResponse<StakeholderAssessment>>(
        `${BASE_PATH}/iro/${iroId}/stakeholder-assessment`,
        data
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to submit assessment');
      return response.data.data!;
    } catch (error: any) {
      // Preserve error code for special handling (e.g., IRO_FINALIZED)
      if (error.response?.data?.code) {
        const customError = new Error(error.response.data.error || 'Errore durante l\'invio della valutazione');
        (customError as any).code = error.response.data.code;
        (customError as any).response = error.response;
        throw customError;
      }
      throw new Error(extractErrorMessage(error, 'Errore durante l\'invio della valutazione'));
    }
  },

  /**
   * Aggiorna una valutazione esistente
   */
  updateAssessment: async (
    iroId: string,
    assessmentId: string,
    data: SubmitStakeholderAssessmentDTO
  ): Promise<StakeholderAssessment> => {
    try {
      const response = await apiClient.put<ApiResponse<StakeholderAssessment>>(
        `${BASE_PATH}/iro/${iroId}/stakeholder-assessment/${assessmentId}`,
        data
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to update assessment');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante l\'aggiornamento della valutazione'));
    }
  },

  /**
   * Conferma la propria valutazione ereditata (carried_over → completed).
   * Se `data` contiene valori modificati, vengono salvati; altrimenti restano
   * quelli ereditati. Unico bottone "Conferma".
   */
  confirmAssessment: async (
    iroId: string,
    data?: SubmitStakeholderAssessmentDTO
  ): Promise<StakeholderAssessment> => {
    try {
      const response = await apiClient.patch<ApiResponse<StakeholderAssessment>>(
        `${BASE_PATH}/iro/${iroId}/stakeholder-assessment/confirm`,
        data ?? {}
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to confirm assessment');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante la conferma della valutazione'));
    }
  },

  /**
   * (Admin) Invia allo stakeholder la notifica per confermare la sua valutazione ereditata
   */
  notifyAssessment: async (iroId: string, assessmentId: string): Promise<{ notifiedAt: string }> => {
    try {
      const response = await apiClient.post<ApiResponse<{ notifiedAt: string }>>(
        `${BASE_PATH}/iro/${iroId}/stakeholder-assessment/${assessmentId}/notify`
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to notify assessment');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante l\'invio della valutazione'));
    }
  },

  /**
   * Conferma tutte le valutazioni ereditate dell'utente per l'anno indicato
   */
  confirmAllAssessments: async (assessmentYear?: number): Promise<{ confirmed: number }> => {
    const qs = new URLSearchParams();
    if (assessmentYear !== undefined) qs.set('year', String(assessmentYear));
    const params = qs.toString() ? `?${qs.toString()}` : '';
    try {
      const response = await apiClient.patch<ApiResponse<{ confirmed: number }>>(
        `${BASE_PATH}/assessments/confirm-all${params}`
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to confirm all');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante la conferma massiva'));
    }
  },

  /**
   * Elimina una valutazione stakeholder (solo admin)
   */
  deleteAssessment: async (iroId: string, assessmentId: string): Promise<void> => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `${BASE_PATH}/iro/${iroId}/stakeholder-assessment/${assessmentId}`
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to delete assessment');
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante l\'eliminazione della valutazione'));
    }
  },

  /**
   * Ottieni tutte le valutazioni per un IRO
   */
  getAssessmentsByIRO: async (iroId: string): Promise<StakeholderAssessmentListResponse> => {
    const response = await apiClient.get<ApiResponse<StakeholderAssessmentListResponse>>(
      `${BASE_PATH}/iro/${iroId}/stakeholder-assessments`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get assessments');
    return response.data.data!;
  },

  /**
   * Ottieni la propria valutazione per un IRO
   */
  getMyAssessment: async (iroId: string): Promise<StakeholderAssessment | null> => {
    const response = await apiClient.get<ApiResponse<StakeholderAssessment | null>>(
      `${BASE_PATH}/iro/${iroId}/stakeholder-assessment/mine`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get my assessment');
    return response.data.data || null;
  },

  /**
   * Ottieni gli IRO assegnati all'utente
   * Con all=true (solo admin) ritorna tutti gli assessment
   */
  getMyAssignedIROs: async (all?: boolean, assessmentYear?: number): Promise<MyAssignedIROsResponse> => {
    const qs = new URLSearchParams();
    if (all) qs.set('all', 'true');
    if (assessmentYear !== undefined) qs.set('year', String(assessmentYear));
    const params = qs.toString() ? `?${qs.toString()}` : '';
    const response = await apiClient.get<ApiResponse<MyAssignedIROsResponse>>(
      `${BASE_PATH}/assessments/my-assigned${params}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get assigned IROs');
    return response.data.data!;
  },

  /**
   * Salta un IRO (rifiuta di fare l'assessment)
   */
  skipIRO: async (iroId: string, reason?: string): Promise<{ skipped: boolean; message: string }> => {
    try {
      const response = await apiClient.post<ApiResponse<{ skipped: boolean; message: string }>>(
        `${BASE_PATH}/iro/${iroId}/skip`,
        { reason }
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to skip IRO');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante lo skip dell\'IRO'));
    }
  },

  /**
   * Lista assessment come entità proprie (non wrapped in AssignedIRO).
   * Ogni elemento è un record StakeholderAssessment con dati IRO popolati.
   * Con all=true (solo admin) ritorna tutti gli assessment dell'azienda.
   */
  listAssessments: async (all?: boolean, assessmentYear?: number): Promise<AssessmentListItem[]> => {
    const qs = new URLSearchParams();
    if (all) qs.set('all', 'true');
    if (assessmentYear !== undefined) qs.set('year', String(assessmentYear));
    const params = qs.toString() ? `?${qs.toString()}` : '';
    const response = await apiClient.get<ApiResponse<AssessmentListItem[]>>(
      `${BASE_PATH}/assessments/list${params}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to list assessments');
    return response.data.data!;
  },

  /**
   * Ottieni la valutazione dell'anno precedente per un IRO
   */
  getPreviousYearAssessment: async (iroId: string, year: number): Promise<StakeholderAssessment | null> => {
    try {
      const response = await apiClient.get<ApiResponse<StakeholderAssessment | null>>(
        `${BASE_PATH}/iro/${iroId}/stakeholder-assessment/previous-year?year=${year}`
      );
      if (!response.data.success) return null;
      return response.data.data || null;
    } catch {
      return null;
    }
  },

  /**
   * Aggiorna nome e/o ruolo di un assessor su tutti i suoi assessment
   */
  updateAssessorInfo: async (
    assessorId: string,
    data: { assessorName?: string; assessorRole?: string; secondaryAssessors?: string[] }
  ): Promise<{ modifiedCount: number }> => {
    try {
      const response = await apiClient.put<ApiResponse<{ modifiedCount: number }>>(
        `${BASE_PATH}/assessor/${assessorId}/info`,
        data
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to update assessor info');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante l\'aggiornamento delle info assessor'));
    }
  },

  /**
   * Consolida i punteggi delle valutazioni
   */
  consolidateScores: async (iroId: string): Promise<any> => {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        `${BASE_PATH}/iro/${iroId}/consolidate-assessments`
      );
      if (!response.data.success) throw new Error(response.data.error || 'Failed to consolidate scores');
      return response.data.data!;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Errore durante il consolidamento dei punteggi'));
    }
  },
};
